// src/slices/sitsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UISit } from '../services/types';
import { sitsService } from '../services/sits.service';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface SitsState {
  // full dataset (used by selectors/KPIs)
  items: UISit[];
  status: Status;
  error?: string;

  // pagination channel (for tables)
  pageData: UISit[]; // current page items
  page: number; // 1-based
  pageSize: number; // items per page
  total: number; // total items
  pageLoading: boolean;
}

const initialState: SitsState = {
  items: [],
  status: 'idle',
  pageData: [],
  page: 1,
  pageSize: 25,
  total: 0,
  pageLoading: false,
};

/** Full list (unchanged) */
export const fetchSits = createAsyncThunk<UISit[]>('sits/fetchAll', async () => {
  return await sitsService.list(); // zero-arg service returning ALL items
});

/** Paged list — loads ALL once (or reuses items) and slices client-side */
export const fetchSitsPage = createAsyncThunk<
  { items: UISit[]; total: number; page: number; pageSize: number },
  { page?: number; pageSize?: number } | void
>('sits/fetchPage', async (args, { getState }) => {
  const state = getState() as { sits: SitsState };
  const page = args?.page ?? state.sits.page ?? 1;
  const pageSize = args?.pageSize ?? state.sits.pageSize ?? 25;
  const offset = (page - 1) * pageSize;

  const all = state.sits.items.length > 0 ? state.sits.items : await sitsService.list();

  const total = all.length;
  const items = all.slice(offset, offset + pageSize);

  return { items, total, page, pageSize };
});

const sitsSlice = createSlice({
  name: 'sits',
  initialState,
  reducers: {
    setSitsPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setSitsPageSize(state, action: PayloadAction<number>) {
      state.pageSize = Math.max(1, action.payload);
      state.page = 1; // reset to first page when page size changes
    },
    resetSitsPagination(state) {
      state.page = 1;
      state.pageSize = initialState.pageSize;
      state.total = 0;
      state.pageData = [];
      state.pageLoading = false;
    },
  },
  extraReducers: (b) => {
    // full list
    b.addCase(fetchSits.pending, (s) => {
      s.status = 'loading';
      s.error = undefined;
    })
      .addCase(fetchSits.fulfilled, (s, a: PayloadAction<UISit[]>) => {
        s.status = 'succeeded';
        s.items = a.payload;
        // optional: seed first page if not set yet
        if (s.total === 0) {
          s.total = a.payload.length;
          s.pageData = a.payload.slice(0, s.pageSize);
        }
      })
      .addCase(fetchSits.rejected, (s, a) => {
        s.status = 'failed';
        s.error = a.error.message;
      });

    // paged channel
    b.addCase(fetchSitsPage.pending, (s) => {
      s.pageLoading = true;
      s.error = undefined;
    })
      .addCase(
        fetchSitsPage.fulfilled,
        (
          s,
          a: PayloadAction<{
            items: UISit[];
            total: number;
            page: number;
            pageSize: number;
          }>,
        ) => {
          s.pageLoading = false;
          s.pageData = a.payload.items;
          s.total = a.payload.total;
          s.page = a.payload.page;
          s.pageSize = a.payload.pageSize;
        },
      )
      .addCase(fetchSitsPage.rejected, (s, a) => {
        s.pageLoading = false;
        s.error = a.error.message;
      });
  },
});

export const { setSitsPage, setSitsPageSize, resetSitsPagination } = sitsSlice.actions;

export default sitsSlice.reducer;
